import React, { useEffect } from "react";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { OrganizationWithUsers } from "@/lib/types";
import { UserList } from "@/components/UserList";
import { api } from "@/lib/api";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";

const AddUserFormSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
});

function OrganizationDetails() {
  const { organizations, selectedOrganizationId, updateOrganization } =
    useOrganizationsStore();
  const [organization, setOrganization] =
    React.useState<OrganizationWithUsers | null>(null);
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = React.useState(false);
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof AddUserFormSchema>>({
    resolver: zodResolver(AddUserFormSchema),
    defaultValues: {
      email: "",
    },
  });

  useEffect(() => {
    if (selectedOrganizationId) {
      const org = organizations.find((o) => o.id === selectedOrganizationId);
      if (org) {
        setOrganization(org);
      } else {
        api
          .getOrganization(selectedOrganizationId)
          .then((orgData) => {
            setOrganization(orgData);
            updateOrganization(selectedOrganizationId, orgData);
          })
          .catch(console.error);
      }
    }
  }, [selectedOrganizationId, organizations, updateOrganization]);

  const onSubmit = async (data: z.infer<typeof AddUserFormSchema>) => {
    if (!selectedOrganizationId) return;
    try {
      const updatedOrg = await api.addUserToOrganization(
        selectedOrganizationId,
        data.email
      );
      setOrganization(updatedOrg);
      updateOrganization(selectedOrganizationId, updatedOrg);
      toast({ title: "User added successfully" });
      form.reset();
      setIsAddUserDialogOpen(false);
    } catch (error) {
      console.error("Failed to add user:", error);
      toast({
        title: "Failed to add user",
        description: String(error),
        variant: "destructive",
      });
    }
  };

  if (!organization) {
    return <div>Loading...</div>;
  }

  return (
    <div className="m-8">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">{organization.name}</h1>
      </div>
      <Card className="w-full">
        <CardHeader className="flex flex-row justify-between items-center mb-4">
          <CardTitle>Users</CardTitle>
          <Dialog
            open={isAddUserDialogOpen}
            onOpenChange={setIsAddUserDialogOpen}
          >
            <DialogTrigger asChild>
              <Button>Add User</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add User</DialogTitle>
                <DialogDescription>
                  Enter the email of the user you wish to add.
                </DialogDescription>
              </DialogHeader>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="user@example.com"
                        {...form.register("email")}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                  <DialogFooter>
                    <Button type="submit">Add User</Button>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <UserList users={organization.users.map((ou) => ou.user)} />
        </CardContent>
      </Card>
    </div>
  );
}

export default OrganizationDetails;
